'use client';
import { useEffect, useRef } from 'react';

/**
 * Amplience Iframe Component
 *
 * Replaces GTM-based content injection. Embeds external content via iframe or HTML injection.
 *
 * @example Publitas Catalog (HTML injection) - MOST COMMON USE CASE
 * ```
 * <AmplienceIframe
 *   html='<div id="publitas-embed-nm0tl2wltv"></div><script data-cfasync="false" data-wrapper-id="publitas-embed-nm0tl2wltv" src="https://view.publitas.com/embed.js"></script>'
 *   width={100}
 *   fullWidth={true}
 * />
 * ```
 * Note: The `id` prop is optional - the injected HTML contains its own IDs.
 *
 * @example Google Maps (iframe with src)
 * ```
 * <AmplienceIframe
 *   src="https://www.google.com/maps/embed?pb=..."
 *   height={450}
 * />
 * ```
 *
 * Security: Only https:// URLs or relative paths are allowed for src.
 * Performance: Uses lazy loading by default (loads only when visible) for iframes.
 *
 * When to use:
 * - ✅ Third-party embeds with scripts (Publitas, GTM replacements) - use `html` prop
 * - ✅ Simple iframe embeds (Google Maps, YouTube) - use `src` prop
 */
interface AmplienceIframeProps {
  // Mode selection: use src OR html (not both)
  src?: string; // HTTPS URL or relative path for iframe embeds
  html?: string; // Raw HTML content to inject (e.g., Publitas embed code with div + script)

  // Optional container ID (the injected HTML can have its own IDs)
  id?: string;

  // Styling options (work for both modes)
  width?: number; // Width as percentage (0-100), defaults to 100
  height?: number; // Height in pixels, defaults to 450px for iframe mode, auto for HTML mode
  fullWidth?: boolean; // Makes container fill 100vw

  // Iframe-specific options (ignored in HTML mode)
  title?: string;
  allowFullscreen?: boolean;
  loading?: 'lazy' | 'eager';
  allow?: string;
}

const AmplienceIframe: React.FC<AmplienceIframeProps> = ({
  src,
  html,
  id,
  width,
  height,
  title = 'Embedded content',
  allowFullscreen = false,
  loading = 'lazy',
  allow,
  fullWidth = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (html && containerRef.current) {
      const container = containerRef.current;
      container.innerHTML = html;

      const scripts = container.querySelectorAll('script');
      if (scripts.length > 0) {
        scripts.forEach((oldScript) => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value);
          });
          if (oldScript.textContent) {
            newScript.textContent = oldScript.textContent;
          }
          oldScript.parentNode?.replaceChild(newScript, oldScript);
        });
      }
    }
  }, [html]);

  if (src) {
    const containerStyles: React.CSSProperties = {
      width: fullWidth ? '100vw' : typeof width === 'number' ? `${width}%` : '100%',
      height: typeof height === 'number' ? `${height}px` : '450px',
      marginLeft: fullWidth ? 'calc(-50vw + 50%)' : undefined,
      marginRight: fullWidth ? 'calc(-50vw + 50%)' : undefined,
      position: 'relative',
      display: 'block',
    };

    return (
      <div style={containerStyles} data-testid="amplience-iframe-container">
        <iframe
          src={src}
          width="100%"
          height="100%"
          title={title}
          allowFullScreen={allowFullscreen}
          loading={loading}
          allow={allow}
          data-testid="amplience-iframe"
          style={{
            border: 'none',
            display: 'block',
          }}
        />
      </div>
    );
  }

  const containerStyles: React.CSSProperties = {
    width: fullWidth ? '100vw' : typeof width === 'number' ? `${width}%` : '100%',
    height: typeof height === 'number' ? `${height}px` : 'auto',
    marginLeft: fullWidth ? 'calc(-50vw + 50%)' : undefined,
    marginRight: fullWidth ? 'calc(-50vw + 50%)' : undefined,
    position: 'relative',
    display: 'block',
  };

  return (
    <div
      id={id}
      ref={containerRef}
      style={containerStyles}
      role="presentation"
      data-testid="amplience-iframe-container"
    />
  );
};

export default AmplienceIframe;
